import { get } from 'lodash'
import { rem } from 'polished'
import React, { useState } from 'react'
import { CUSTOM_CONTENT } from 'shared-constants'
import styled from 'styled-components'
import { type GridProps, type SpaceProps } from 'styled-system'
import { Image, IMAGE_LAYOUT } from 'components/Image'
import { Grid } from 'components/Layout'
import { ReadMoreText } from 'components/ReadMoreText'
import { H4, Text } from 'components/Typography'
import {
  COLOR_INTENT,
  FONT_SIZE,
  FONT_STACK,
  IMAGE_BASE_WIDTH,
  mq,
  RADIUS,
  SPACE,
} from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'
import { useMedia } from 'useMedia'

const IMAGE_WIDTH = { MOBILE: rem(56), TABLET: rem(76) }
const RECOMMENDATION_MAX_LENGTH = { MOBILE: 140, TABLET: 500 }

const Photo = styled(Image)`
  border-radius: ${RADIUS.CIRCLE};
`

const StyledReadMoreText = styled(ReadMoreText)`
  ${mq.to.TABLET`
      grid-column: 1/3;
  `}
`

const ReadMoreButton = styled(Text)`
  cursor: pointer;
`

interface ProductAskExpertProps extends GridProps, SpaceProps {
  customContent: PublicProductQuery['product']['variants'][0]['customContent']
}

export const ProductAskExpert: React.FC<React.PWC<ProductAskExpertProps>> = ({
  customContent,
  ...rest
}) => {
  const { MOBILE: isMobile } = useMedia()
  const [isCopyExpanded, setIsCopyExpanded] = useState(false)
  const askExpertData = get(customContent, CUSTOM_CONTENT.ASK_EXPERT, null)
  const askExpertCopy = get(customContent, CUSTOM_CONTENT.ASK_EXPERT_COPY)
  const imageAttachment =
    askExpertData?.headshotImageAttachment ||
    askExpertData?.profileImageAttachment

  if (!askExpertData) {
    return null
  }
  return (
    <Grid
      gridTemplateColumns={{
        MOBILE: `${IMAGE_WIDTH.MOBILE} 1fr`,
        TABLET: `${IMAGE_WIDTH.TABLET} 1fr`,
      }}
      gridGap={{ MOBILE: SPACE.PX_15, TABLET: SPACE.PX_20 }}
      alignItems={{ MOBILE: 'center', TABLET: 'start' }}
      {...rest}
      my={SPACE.PX_22}
    >
      <Grid gridRow={{ MOBILE: '1/2', TABLET: '1/3' }}>
        <Photo
          baseWidth={IMAGE_BASE_WIDTH.PX_320}
          layout={IMAGE_LAYOUT.RESPONSIVE}
          alt={imageAttachment?.alt || askExpertData.profileName}
          src={imageAttachment?.url}
          sizes={`(max-width: 768px) ${IMAGE_WIDTH.MOBILE}, ${IMAGE_WIDTH.TABLET}`}
        />
      </Grid>
      <H4
        mb="0"
        fontFamily={FONT_STACK.SERIF}
        fontSize={{ MOBILE: FONT_SIZE.PX_25, TABLET: FONT_SIZE.PX_24 }}
      >
        {`${askExpertData.profileName} recommends:`}
      </H4>
      <StyledReadMoreText
        text={`&lsquo;${askExpertCopy}&rsquo;`}
        fontSize={FONT_SIZE.PX_14}
        maxAllowedLength={
          isMobile
            ? RECOMMENDATION_MAX_LENGTH.MOBILE
            : RECOMMENDATION_MAX_LENGTH.TABLET
        }
        MarkdownRendererProps={{
          fontFamily: FONT_STACK.SANS,
        }}
        isExpanded={isCopyExpanded}
        actionButton={() => (
          <ReadMoreButton
            color={COLOR_INTENT.BRAND}
            fontSize={FONT_SIZE.PX_14}
            onClick={() => setIsCopyExpanded((prevState) => !prevState)}
          >
            {isCopyExpanded ? 'Show less' : 'Read More'}
          </ReadMoreButton>
        )}
      />
    </Grid>
  )
}
